import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const FeatureToggleContext = createContext(null);

export const useFeatureToggle = () => {
  const context = useContext(FeatureToggleContext);
  if (!context) {
    throw new Error('useFeatureToggle must be used within a FeatureToggleProvider');
  }
  return context;
};

export const useFeature = (featureKey) => {
  const { isFeatureEnabled, loading } = useFeatureToggle();
  return {
    enabled: isFeatureEnabled(featureKey),
    loading
  };
};

export const FeatureToggleProvider = ({ children }) => {
  const [features, setFeatures] = useState([]);
  const [enabledFeatures, setEnabledFeatures] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Fetch enabled features from API
  const fetchEnabledFeatures = useCallback(async () => {
    try {
      const response = await axios.get('/api/feature-toggles/enabled');
      
      if (response.data.success) {
        const enabled = new Set(response.data.features);
        setEnabledFeatures(enabled);
        setLastFetch(new Date());
        setError(null);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Feature toggles loaded:', response.data.features);
        }
      }
    } catch (err) {
      console.error('Failed to fetch feature toggles:', err);
      setError(err.message);
      // On error, keep previous state (graceful degradation)
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch full feature details (for admin UI)
  const fetchAllFeatures = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await axios.get('/api/feature-toggles', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setFeatures(response.data.features);
      }
    } catch (err) {
      console.error('Failed to fetch all features:', err);
      // Silently fail for non-admin users
    }
  }, []);

  // Check if a feature is enabled
  const isFeatureEnabled = useCallback((featureKey) => {
    if (!featureKey) return false;
    return enabledFeatures.has(featureKey);
  }, [enabledFeatures]);

  // Enable a feature (admin only)
  const enableFeature = useCallback(async (featureKey) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/feature-toggles/${featureKey}/enable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Refresh feature list
        await fetchEnabledFeatures();
        await fetchAllFeatures();
        return { success: true };
      }
      
      return response.data;
    } catch (err) {
      console.error(`Failed to enable feature ${featureKey}:`, err);
      return { success: false, message: err.response?.data?.message || err.message };
    }
  }, [fetchEnabledFeatures, fetchAllFeatures]);

  // Disable a feature (admin only)
  const disableFeature = useCallback(async (featureKey) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/feature-toggles/${featureKey}/disable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Refresh feature list
        await fetchEnabledFeatures();
        await fetchAllFeatures();
        return { success: true };
      }
      
      return response.data;
    } catch (err) {
      console.error(`Failed to disable feature ${featureKey}:`, err);
      return { success: false, message: err.response?.data?.message || err.message };
    }
  }, [fetchEnabledFeatures, fetchAllFeatures]);

  // Refresh cache manually
  const refreshCache = useCallback(async () => {
    await fetchEnabledFeatures();
  }, [fetchEnabledFeatures]);

  // Initial fetch on mount
  useEffect(() => {
    fetchEnabledFeatures();
  }, [fetchEnabledFeatures]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchEnabledFeatures();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [fetchEnabledFeatures]);

  // Refresh when tab becomes visible (using Page Visibility API)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh if last fetch was more than 30 seconds ago
        const now = new Date();
        if (!lastFetch || (now - lastFetch) > 30000) {
          fetchEnabledFeatures();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchEnabledFeatures, lastFetch]);

  const value = {
    features,
    enabledFeatures: Array.from(enabledFeatures),
    loading,
    error,
    isFeatureEnabled,
    enableFeature,
    disableFeature,
    refreshCache,
    fetchAllFeatures,
    lastFetch
  };

  return (
    <FeatureToggleContext.Provider value={value}>
      {children}
    </FeatureToggleContext.Provider>
  );
};

export default FeatureToggleContext;

